import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  AlertCircle, AlertTriangle, Calendar, CheckCircle2, ChevronDown, ChevronRight, ChevronUp,
  FileText, Loader2, Package, Play, Printer, RefreshCw, X,
} from 'lucide-react';
import { supabase } from '../../supabase';
import { useEmpresaId } from '../../context/EmpresaContext';
import type {
  AppBoleta, AppBoletaProducto, AppBoom,
} from '../../types/empacadora';
import { inputCls, labelCls, pageTitleCls, pageSubtitleCls } from '../../components/ui';
import { imprimirBoletas } from './boletaImprimir';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtF(s?: string | null) {
  if (!s) return '—';
  return new Date(s + 'T00:00:00').toLocaleDateString('es-CR', {
    day: '2-digit', month: '2-digit', year: '2-digit',
  });
}

function fmtN(n?: number | null, dec = 2) {
  if (n == null) return '—';
  return n.toLocaleString('es-CR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

/** Calcula semana ISO estilo VFP: WW-AA */
function semanaLabel(fecha: Date): string {
  const tmp = new Date(Date.UTC(fecha.getFullYear(), fecha.getMonth(), fecha.getDate()));
  tmp.setUTCDate(tmp.getUTCDate() + 4 - (tmp.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((tmp.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  const yy = String(fecha.getFullYear()).slice(-2);
  return `${String(week).padStart(2, '0')}-${yy}`;
}

/** Suma 6 días a una fecha ISO string */
function sumar6Dias(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + 6);
  return d.toISOString().slice(0, 10);
}

function fechaLocalISO(fecha = new Date()): string {
  const yyyy = fecha.getFullYear();
  const mm = String(fecha.getMonth() + 1).padStart(2, '0');
  const dd = String(fecha.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function semanaDesdeISO(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  if (d.getDay() === 0) d.setDate(d.getDate() + 1);
  return semanaLabel(d);
}

/** Nombre del día de la semana */
function diaSemana(iso: string) {
  return new Date(iso + 'T00:00:00').toLocaleDateString('es-CR', { weekday: 'long' });
}

function naturalCompare(a: string | number | null | undefined, b: string | number | null | undefined) {
  return String(a ?? '').localeCompare(String(b ?? ''), 'es-CR', {
    numeric: true,
    sensitivity: 'base',
  });
}

function legacyLoteCompare(a: string | number | null | undefined, b: string | number | null | undefined) {
  // El orden de referencia VFP viene por lote como texto: 1, 11, 12, ..., 3, 31, ...
  return String(a ?? '').localeCompare(String(b ?? ''), 'es-CR', {
    numeric: false,
    sensitivity: 'base',
  });
}

const ESTADO_CFG: Record<string, { label: string; bg: string; border: string; text: string }> = {
  borrador:  { label: 'Borrador',  bg: '#e8eaf6',                 border: '#9fa8da',                    text: '#3949ab' },
  pendiente: { label: 'Pendiente', bg: 'var(--tone-warn-bg)',     border: 'var(--tone-warn-border)',     text: 'var(--tone-warn-text)' },
  impresa:   { label: 'Impresa',   bg: 'var(--tone-info-bg)',     border: 'var(--tone-info-border)',     text: 'var(--tone-info-text)' },
  aplicada:  { label: 'Aplicada',  bg: 'var(--tone-success-bg)',  border: 'var(--tone-success-border)',  text: 'var(--tone-success-text)' },
  cancelada: { label: 'Cancelada', bg: 'var(--surface-deep)',     border: 'var(--line)',                 text: 'var(--ink-faint)' },
};

// ─── Toast ────────────────────────────────────────────────────────────────────

function Toast({ msg, ok, onClose }: { msg: string; ok: boolean; onClose: () => void }) {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  return ReactDOM.createPortal(
    <div className="fixed bottom-5 right-5 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl text-sm font-medium"
      style={{
        background: ok ? 'var(--tone-success-bg)' : 'var(--tone-warn-bg)',
        border: `1px solid ${ok ? 'var(--tone-success-border)' : 'var(--tone-warn-border)'}`,
        color: ok ? 'var(--tone-success-text)' : 'var(--tone-warn-text)',
      }}>
      {msg}
      <button onClick={onClose}><X size={14} /></button>
    </div>,
    document.body
  );
}

// ─── Lógica de generación ─────────────────────────────────────────────────────

interface BloqueConPlan {
  bloque_id: string;
  bloque_num: string;
  lote_id: string;
  lote_num: string;
  finca_nombre: string;
  fecha_siembra: string;
  estadio_vfp: string;
  plant_count: number;
  area_ha: number;
  grupo_forza: string;
  // plan
  ciclo_id: number;
  ciclo_nombre: string;
  ciclo_dias: number;
  boom_id: number | null;
  boom_nombre: string;
  boom_capacidad_l: number;
  agua_l: number;
  etapa_id: number;
  etapa_nombre: string;
  // calculado
  fecha_plan: string;
  fecha_aplica: string;
  semana: string;
}

interface GrupoBoleta {
  lote_id: string;
  lote_num: string;
  finca_nombre: string;
  ciclo_id: number;
  ciclo_nombre: string;
  etapa_id: number;
  etapa_nombre: string;
  boom_id: number | null;
  boom_nombre: string;
  boom_capacidad_l: number;
  agua_l: number;
  dias: number;
  fecha_siembra: string;
  fecha_plan: string;
  fecha_aplica: string;
  semana: string;
  area_ha: number;
  plantas: number;
  bloques: string;
  bloque_ids: string[];
  grupo_forza: string;
}

type ProgresoGen = { paso: number; total: number; label: string };

// Reporte de validación post-generación. El ERP es autónomo (sin VFP): esta
// validación detecta sola los casos que antes solo se cazaban comparando con VFP.
type AnomaliaLote   = { lote_num: string; finca: string; bloques_activos: number; estadios: string };
type AnomaliaBloque = { lote_num: string; bloque_num: string; estadio: string; motivo: string };
type ResumenLote    = { lote_num: string; finca: string; bloques_activos: number; bloques_en_boleta: number; area_activa: number; area_en_boleta: number };
type ValidacionGen = {
  lotes_sin_boleta: AnomaliaLote[];      // lote con bloques activos pero 0 boletas (caso lote 73)
  bloques_excluidos: AnomaliaBloque[];   // bloque activo que no entró en ninguna boleta (caso lote 58)
  fechas_sospechosas: AnomaliaBloque[];  // fecha ancla vacía o días fuera de rango razonable
  resumen_lotes: ResumenLote[];          // por lote: activos vs incluidos, área activa vs en boleta
};

async function generarBoletas(
  empresaId: number,
  fechaDesde: string,
  fechaHasta: string,
  _semLabelUnused: string,
  boomSel: { id: number; nombre: string; capacidad_l: number } | null,
  onProgress?: (p: ProgresoGen) => Promise<void>,
): Promise<{ ok: boolean; count: number; error?: string; debug?: string; validacion?: ValidacionGen }> {

  await onProgress?.({ paso: 0, total: 1, label: 'Cargando bloques y estadios…' });
  // Cargar configuración de estadios VFP (varfecha dinámica desde BD)
  const { data: estadiosVfp } = await supabase
    .from('app_estadios')
    .select('codigo, varfecha')
    .eq('empresa_id', empresaId);

  const varfechaMap: Record<string, string> = {};
  for (const e of (estadiosVfp ?? [])) {
    varfechaMap[e.codigo] = e.varfecha;
  }
  // Fallback para los campos conocidos si la tabla aún no fue migrada
  if (Object.keys(varfechaMap).length === 0) {
    Object.assign(varfechaMap, {
      DPC: 'fecha_siembra', FPC: 'fecha_induccion', DRC: 'fecha_segunda',
      SRC: 'fecha_semillero', SPC: 'fecha_semillero', BAR: 'fecha_siembra',
    });
  }

  // 1a. Cargar bloques activos (sin join anidado — dos queries separadas)
  const { data: bloques, error: bErr } = await supabase
    .from('emp_bloques')
    .select('id, num, lote_id, fecha_siembra, fecha_induccion, fecha_segunda, fecha_semillero, fecha_forza, fecha_maduracion, fecha_cosecha, fecha_barbecho, fecha_induccion2, fecha_forza2, fecha_maduracion2, fecha_cosecha2, plant_count, area_ha, grupo_forza, ciclo, estadio_vfp')
    .eq('empresa_id', empresaId)
    .eq('activo', true)
    .not('estadio_vfp', 'is', null)
    .not('lote_id', 'is', null);

  if (bErr) return { ok: false, count: 0, error: `bloques: ${bErr.message}` };
  if (!bloques || bloques.length === 0)
    return { ok: false, count: 0, error: 'No hay bloques activos con fecha_siembra y lote asignado' };

  await onProgress?.({ paso: 0, total: 1, label: 'Cargando lotes y plan de manejo…' });
  // 1b. Cargar únicamente lotes activos para resolver nombre y finca.
  // Puede haber bloques que sigan activos dentro de un lote dado de baja; esos
  // bloques no deben generar boletas ni alertas de validación.
  const loteIds = [...new Set(bloques.map(b => b.lote_id).filter(Boolean))];
  const { data: lotes, error: lErr } = await supabase
    .from('emp_lotes')
    .select('id, numero, finca_id, finca:emp_fincas(nombre)')
    .in('id', loteIds)
    .eq('activo', true);

  if (lErr) return { ok: false, count: 0, error: `lotes: ${lErr.message}` };

  const loteMap = new Map<string, { numero: string; finca_nombre: string }>();
  for (const l of (lotes ?? [])) {
    const finca = (l.finca as any);
    loteMap.set(l.id, { numero: l.numero, finca_nombre: finca?.nombre ?? '' });
  }

  // 2. Cargar plan completo (ciclos activos con etapas y booms)
  const { data: ciclos, error: cErr } = await supabase
    .from('app_plan_ciclos')
    .select('id, nombre, dias, agua_l, etapa_id, boom_id, activo')
    .eq('empresa_id', empresaId)
    .eq('activo', true);

  if (cErr) return { ok: false, count: 0, error: `ciclos: ${cErr.message}` };
  if (!ciclos || ciclos.length === 0)
    return { ok: false, count: 0, error: 'No hay ciclos activos en el Plan de Manejo' };

  // 2b. Cargar etapas y booms por separado
  const { data: etapas, error: eErr } = await supabase
    .from('app_plan_etapas')
    .select('id, nombre, activo, ciclo_cosecha, estadio_vfp')
    .eq('empresa_id', empresaId);

  const { data: booms, error: boErr } = await supabase
    .from('app_booms')
    .select('id, nombre, capacidad_l')
    .eq('empresa_id', empresaId)
    .eq('activo', true);

  const etapaMap = new Map((etapas ?? []).map(e => [e.id, e]));
  const boomMap  = new Map((booms  ?? []).map(b => [b.id, b]));

  const f1 = new Date(fechaDesde + 'T00:00:00');
  const f2 = new Date(fechaHasta + 'T23:59:59');

  await onProgress?.({ paso: 0, total: 1, label: 'Calculando fechas de aplicación…' });
  // 3. Cruzar bloques × ciclos: fecha_plan = fecha_siembra + dias
  const candidatos: BloqueConPlan[] = [];

  for (const b of bloques) {
    const loteInfo = loteMap.get(b.lote_id);
    if (!loteInfo) continue;

    const estadioVfp = (b as any).estadio_vfp as string | undefined;

    for (const c of ciclos) {
      const etapa = etapaMap.get(c.etapa_id);
      if (!etapa?.activo) continue;

      // Filtrar: el estadio_vfp del bloque debe coincidir exactamente con el de la etapa.
      // El mapeo estadio→etapa vive en la BD (app_plan_etapas.estadio_vfp), no hardcodeado aquí.
      const etapaEstadio = (etapa as any).estadio_vfp as string | undefined;
      if (!estadioVfp || !etapaEstadio || etapaEstadio !== estadioVfp) continue;

      // Fecha ancla según varfecha del estadio_vfp: cargada desde app_estadios (configurable en UI)
      const fechaAncla: string | null = (b as any)[varfechaMap[estadioVfp] ?? 'fecha_siembra'] ?? null;
      if (!fechaAncla) continue;

      // boom puede ser null — usar capacidad_l=0 como fallback (ciclos manuales sin boom)
      const boom = c.boom_id ? boomMap.get(c.boom_id) : null;
      const boom_cap  = boom ? Number(boom.capacidad_l) : 0;
      const boom_nom  = boom ? (boom as any).nombre ?? '' : '';

      const fp = new Date(fechaAncla + 'T00:00:00');
      fp.setDate(fp.getDate() + Number(c.dias));

      if (fp >= f1 && fp <= f2) {
        // Semana desde fecha_plan — si es domingo usar lunes siguiente (semana empieza domingo pero ISO desde lunes)
        const fpSem = new Date(fp);
        if (fpSem.getDay() === 0) fpSem.setDate(fpSem.getDate() + 1);
        const semBloque = semanaLabel(fpSem);
        candidatos.push({
          bloque_id:        b.id,
          bloque_num:       b.num,
          lote_id:          b.lote_id,
          lote_num:         loteInfo.numero,
          finca_nombre:     loteInfo.finca_nombre,
          fecha_siembra:    (b as any).fecha_siembra ?? fechaAncla,
          estadio_vfp:      estadioVfp ?? '',
          plant_count:      b.plant_count ?? 0,
          area_ha:          Number(b.area_ha ?? 0),
          grupo_forza:      b.grupo_forza ?? '',
          ciclo_id:         c.id,
          ciclo_nombre:     c.nombre,
          ciclo_dias:       Number(c.dias),
          boom_id:          c.boom_id ?? null,
          boom_nombre:      boom_nom,
          boom_capacidad_l: boom_cap,
          agua_l:           Number(c.agua_l ?? 0),
          etapa_id:         etapa.id,
          etapa_nombre:     etapa.nombre,
          fecha_plan:       fp.toISOString().slice(0, 10),
          fecha_aplica:     fp.toISOString().slice(0, 10),
          semana:           semBloque,
        });
      }
    }
  }

  if (candidatos.length === 0)
    return { ok: true, count: 0, debug: `bloques=${bloques.length} ciclos=${ciclos.length} — ninguno cruza en ${fechaDesde}..${fechaHasta}` };

  await onProgress?.({ paso: 0, total: 1, label: 'Agrupando boletas…' });
  // 4. Agrupar por lote + estadio_vfp + ciclo_id + fecha_plan
  // fecha_plan varía cuando bloques del mismo lote tienen diferente fecha ancla
  // (ej: FPC con bloques en grupos_forza distintos → fecha_induccion distintas → boletas separadas)
  const grupos = new Map<string, GrupoBoleta>();
  const candidatosOrdenados = [...candidatos].sort((a, b) =>
    legacyLoteCompare(a.lote_num, b.lote_num) ||
    naturalCompare(a.estadio_vfp, b.estadio_vfp) ||
    naturalCompare(a.ciclo_id, b.ciclo_id) ||
    naturalCompare(a.fecha_plan, b.fecha_plan) ||
    naturalCompare(a.bloque_num, b.bloque_num)
  );

  for (const c of candidatosOrdenados) {
    const key = `${c.lote_id}::${c.estadio_vfp}::${c.ciclo_id}::${c.fecha_plan}`;
    if (!grupos.has(key)) {
      grupos.set(key, {
        lote_id:          c.lote_id,
        lote_num:         c.lote_num,
        finca_nombre:     c.finca_nombre,
        ciclo_id:         c.ciclo_id,
        ciclo_nombre:     c.ciclo_nombre,
        etapa_id:         c.etapa_id,
        etapa_nombre:     c.etapa_nombre,
        boom_id:          c.boom_id,
        boom_nombre:      c.boom_nombre,
        boom_capacidad_l: c.boom_capacidad_l,
        agua_l:           c.agua_l,
        dias:             c.ciclo_dias,
        fecha_siembra:    c.fecha_siembra,
        fecha_plan:       c.fecha_plan,
        fecha_aplica:     c.fecha_aplica,
        semana:           c.semana,
        area_ha:          0,
        plantas:          0,
        bloques:          '',
        bloque_ids:       [],
        grupo_forza:      '',
      });
    }
    const g = grupos.get(key)!;
    g.area_ha  += c.area_ha;
    g.plantas  += c.plant_count;
    g.bloque_ids.push(c.bloque_id);
    g.bloques = [...new Set([...g.bloques.split(',').filter(Boolean), c.bloque_num])]
      .sort(naturalCompare)
      .join(',');
    // Grupo(s) de forza de la boleta: lista única de los bloques (puede haber varios).
    if (c.grupo_forza) {
      g.grupo_forza = [...new Set([...g.grupo_forza.split(', ').filter(Boolean), c.grupo_forza])]
        .sort(naturalCompare)
        .join(', ');
    }
  }

  // 4b. VALIDACIÓN: comparar bloques activos vs los que entraron en boleta.
  //     El ERP es autónomo: esto detecta solo los faltantes (lote sin boleta,
  //     bloque excluido, fecha ancla sospechosa) sin depender de VFP.
  const validacion = construirValidacion(bloques, candidatos, loteMap, etapaMap, ciclos, varfechaMap, fechaHasta);

  await onProgress?.({ paso: 0, total: 1, label: 'Cargando productos del plan…' });
  // 5. Cargar productos del plan para cada ciclo involucrado
  const cicloIds = [...new Set(candidatos.map(c => c.ciclo_id))];
  const { data: productos, error: pErr } = await supabase
    .from('app_plan_productos')
    .select('*')
    .eq('empresa_id', empresaId)
    .in('ciclo_id', cicloIds)
    .eq('activo', true)
    .order('orden');

  if (pErr) return { ok: false, count: 0, error: pErr.message };

  // 5b. Catálogo (inv_productos) como RESPALDO de campos agronómicos cuando el plan
  //     los tiene vacíos. inv_productos es la fuente única de justificación/latencia/
  //     reingreso/departamento/grupo (ver decisión 24/05). Match por codigo = articulo.
  const articulosPlan = [...new Set((productos ?? []).map((p: any) => p.articulo).filter(Boolean))];
  const catalogo: Record<string, any> = {};
  if (articulosPlan.length > 0) {
    const { data: invs } = await supabase
      .from('inv_productos')
      .select('id, codigo, justificacion, latencia_dias, reingreso, departamento, grupo_quimico, ingrediente_activo')
      .eq('empresa_id', empresaId)
      .in('codigo', articulosPlan);
    for (const iv of (invs ?? [])) catalogo[(iv as any).codigo] = iv;
  }

  // 6. Insertar boletas y productos en Supabase
  const totalGrupos = grupos.size;
  let count = 0;
  const gruposOrdenados = [...grupos.values()].sort((a, b) =>
    legacyLoteCompare(a.lote_num, b.lote_num) ||
    naturalCompare(a.etapa_nombre, b.etapa_nombre) ||
    naturalCompare(a.ciclo_id, b.ciclo_id) ||
    naturalCompare(a.fecha_plan, b.fecha_plan)
  );

  await onProgress?.({ paso: 0, total: totalGrupos, label: `Preparando ${totalGrupos} boleta${totalGrupos === 1 ? '' : 's'} para guardar…` });

  for (const g of gruposOrdenados) {
    await onProgress?.({ paso: count, total: totalGrupos, label: `Guardando boleta ${count + 1} de ${totalGrupos}…` });
    // Obtener número de boleta (función SQL thread-safe)
    const { data: numData, error: numErr } = await supabase
      .rpc('app_next_boleta', { p_empresa_id: empresaId });
    if (numErr) return { ok: false, count, error: numErr.message };

    // Boom seleccionado por el operador antes de generar (global para toda la semana)
    const boom_cap   = boomSel ? Number(boomSel.capacidad_l) : 0;
    const boom_nom   = boomSel ? boomSel.nombre : null;
    const boom_id    = boomSel ? boomSel.id : null;
    const agua_total = g.agua_l * g.area_ha;
    const tiene_boom = boom_cap > 0;

    const boleta: Omit<AppBoleta, 'id' | 'created_at'> = {
      empresa_id:        empresaId,
      numero:            numData as string,
      semana:            g.semana,          // calculada desde fecha_plan real (punto 1)
      fecha_desde:       fechaDesde,
      fecha_hasta:       fechaHasta,
      lote_id:           g.lote_id,
      lote_num:          g.lote_num,
      finca_nombre:      g.finca_nombre,
      bloques:           g.bloques,
      grupo_forza:       g.grupo_forza || null,   // grupo(s) de forza para RE-08
      fecha_siembra:     g.fecha_siembra,
      dias:              g.dias,
      area_ha:           g.area_ha,         // suma solo bloques candidatos (punto 3)
      plantas:           g.plantas,          // idem (punto 2)
      densidad:          g.area_ha > 0 ? g.plantas / g.area_ha : 0,
      etapa_id:          g.etapa_id,
      etapa_nombre:      g.etapa_nombre,
      ciclo_id:          g.ciclo_id,
      ciclo_nombre:      g.ciclo_nombre,
      boom_id:           boom_id,
      boom_nombre:       boom_nom,
      boom_capacidad_l:  boom_cap || null,
      agua_l:            g.agua_l,
      n_booms_completos: tiene_boom ? Math.floor(agua_total / boom_cap) : null,
      litros_fraccion:   tiene_boom ? agua_total % boom_cap : null,
      agua_total_l:      agua_total,
      manual:            false,
      estado:            'borrador',
      fecha_plan:        g.fecha_plan,
      fecha_aplicacion:  null,
    };

    const { data: boletaIns, error: bIns } = await supabase
      .from('app_boletas')
      .insert(boleta)
      .select('id')
      .single();

    if (bIns || !boletaIns) return { ok: false, count, error: `insert boleta: ${bIns?.message} | code: ${bIns?.code} | details: ${bIns?.details}` };

    const boletaId = (boletaIns as any).id as number;

    // Productos del plan para este ciclo
    const prods = (productos ?? []).filter((p: any) => p.ciclo_id === g.ciclo_id);
    const lineas: Omit<AppBoletaProducto, 'id' | 'created_at'>[] = prods.map((p: any) => {
      // Cantidad Has = dosis × area (fórmula VFP exacta)
      const dosisTotal = p.dosis * g.area_ha;
      // Tasa por UN boom completo. Si el producto no trae volumen de referencia,
      // el que manda es el agua del ciclo (usar 1 dispararía la cantidad por
      // boom a dosis × capacidad).
      const boomPorBoom = tiene_boom && g.agua_l > 0
        ? (p.dosis / (Number(p.volumen) || g.agua_l)) * boom_cap
        : 0;
      // Cantidad x Boom solo se llena cuando el agua total alcanza para un
      // boom completo — si no, es 0 y toda la dosis va en la fracción.
      const boomProd   = agua_total >= boom_cap ? boomPorBoom : 0;
      // Fracción = MOD(dosisTotal, boomPorBoom): con boom completo da el
      // remanente; sin boom completo da directamente dosisTotal (toda la dosis
      // va en la única tanqueada parcial) — por eso usa boomPorBoom, no boomProd.
      const fraccion   = boomPorBoom > 0 ? dosisTotal % boomPorBoom : 0;
      // Respaldo desde catálogo (inv_productos) cuando el plan tiene el campo vacío.
      const inv = catalogo[p.articulo] || {};
      const blank = (v: any) => v === null || v === undefined || String(v).trim() === '';
      const justif = !blank(p.justificacion) ? p.justificacion : (inv.justificacion ?? null);
      const depto  = !blank(p.departamento)  ? p.departamento  : (inv.departamento ?? null);
      const grupo  = !blank(p.grupo_quimico) ? p.grupo_quimico : (inv.grupo_quimico ?? null);
      const ingrediente = !blank(p.ingrediente_activo) ? p.ingrediente_activo : (inv.ingrediente_activo ?? null);
      // Regla G.A.P.: latencia/reingreso SIEMPRE con valor — si no hay dato, 'N/A' (no vacío).
      // El plan guarda latencia numérica (24); el catálogo latencia_dias numérica. Si es
      // un número puro se muestra como "N DIAS"; si ya trae texto ('15 DIAS') se respeta.
      const fmtLatencia = (v: any) => /^\d+$/.test(String(v).trim()) ? `${v} DIAS` : String(v);
      const latenc = !blank(p.latencia)
        ? fmtLatencia(p.latencia)
        : (!blank(inv.latencia_dias) ? `${inv.latencia_dias} DIAS` : 'N/A');
      const reing  = !blank(p.reingreso)     ? p.reingreso     : (!blank(inv.reingreso) ? inv.reingreso : 'N/A');
      return {
        empresa_id:        empresaId,
        boleta_id:         boletaId,
        plan_producto_id:  p.id,
        inv_producto_id:   inv.id ?? null,
        articulo:          p.articulo,
        nombre_producto:   p.nombre_producto,
        descripcion:       p.descripcion ?? null,
        justificacion:     justif,
        grupo_quimico:     grupo,
        departamento:      depto,
        ingrediente_activo: ingrediente,
        medida:            p.medida,
        reingreso:         reing,
        latencia:          latenc,
        orden:             p.orden,
        dosis:             p.dosis,
        volumen:           p.volumen ?? null,
        cantidad_aplicar:  dosisTotal,              // dosis × area (punto 7)
        cantidad_boom:     boomProd,
        cantidad_fraccion: fraccion,
        es_agua:           false,
      };
    });

    // Línea AGUA al final (articulo='999')
    lineas.push({
      empresa_id:        empresaId,
      boleta_id:         boletaId,
      articulo:          '999',
      nombre_producto:   'AGUA REQUERIDA',
      descripcion:       `Volumen de fórmula ${g.agua_l} Lts.`,
      medida:            'LT',
      orden:             9999,
      dosis:             g.agua_l,
      volumen:           g.agua_l,
      cantidad_aplicar:  agua_total,
      cantidad_boom:     tiene_boom && agua_total >= boom_cap ? boom_cap : 0,
      cantidad_fraccion: tiene_boom ? agua_total % boom_cap : 0,
      es_agua:           true,
    });

    if (lineas.length > 0) {

      const { error: lErr } = await supabase.from('app_boleta_productos').insert(lineas);

      if (lErr) return { ok: false, count, error: `insert productos: ${lErr.message} | code: ${lErr.code} | details: ${lErr.details}` };
    }

    count++;
    await onProgress?.({ paso: count, total: totalGrupos, label: `Guardadas ${count} de ${totalGrupos} boletas…` });
  }

  return { ok: true, count, validacion };
}

// Construye el reporte de validación comparando bloques activos vs los que
// entraron en boleta. No depende de VFP — el ERP se autovalida.
function construirValidacion(
  bloques: any[],
  candidatos: BloqueConPlan[],
  loteMap: Map<string, { numero: string; finca_nombre: string }>,
  etapaMap: Map<number, any>,
  ciclos: any[],
  varfechaMap: Record<string, string>,
  fechaHasta: string,
): ValidacionGen {
  const idsEnBoleta = new Set(candidatos.map(c => c.bloque_id));
  // Estadios que tienen al menos un ciclo activo con etapa activa (si no, es normal que no genere)
  const estadiosConCiclo = new Set<string>();
  for (const c of ciclos) {
    const et = etapaMap.get(c.etapa_id);
    const estadio = et?.activo ? (et as any).estadio_vfp as string | undefined : undefined;
    if (!estadio) continue;
    estadiosConCiclo.add(estadio);
  }

  const bloques_excluidos: AnomaliaBloque[] = [];
  const fechas_sospechosas: AnomaliaBloque[] = [];
  // resumen + lotes sin boleta: acumular por lote
  const porLote = new Map<string, { finca: string; activos: number; enBoleta: number; areaAct: number; areaBol: number; estadios: Set<string>; sospechoso: boolean }>();

  for (const b of bloques) {
    const li = loteMap.get(b.lote_id);
    if (!li) continue;
    const est = (b as any).estadio_vfp as string | undefined;
    const area = Number(b.area_ha ?? 0);
    const entro = idsEnBoleta.has(b.id);

    const acc = porLote.get(li.numero) ?? { finca: li.finca_nombre, activos: 0, enBoleta: 0, areaAct: 0, areaBol: 0, estadios: new Set<string>(), sospechoso: false };
    acc.activos++; acc.areaAct += area;
    if (entro) { acc.enBoleta++; acc.areaBol += area; }
    if (est) acc.estadios.add(est);

    // Solo es ANOMALÍA si el bloque NO entró Y su fecha ancla está mal (vacía o días
    // absurdos). Que un bloque "no cruce ningún ciclo esta semana" es NORMAL (el ciclo
    // no toca) y NO se reporta — así el panel muestra señal, no ruido.
    if (!entro && est && estadiosConCiclo.has(est)) {
      const ancla = (b as any)[varfechaMap[est] ?? 'fecha_siembra'] ?? null;
      if (!ancla) {
        const motivo = `sin fecha ancla (${varfechaMap[est] ?? 'fecha_siembra'})`;
        fechas_sospechosas.push({ lote_num: li.numero, bloque_num: String(b.num), estadio: est, motivo });
        bloques_excluidos.push({ lote_num: li.numero, bloque_num: String(b.num), estadio: est, motivo });
        acc.sospechoso = true;
      }
    }
    porLote.set(li.numero, acc);
  }

  const lotes_sin_boleta: AnomaliaLote[] = [];
  const resumen_lotes: ResumenLote[] = [];
  for (const [lote_num, v] of porLote) {
    resumen_lotes.push({ lote_num, finca: v.finca, bloques_activos: v.activos, bloques_en_boleta: v.enBoleta, area_activa: Math.round(v.areaAct * 100) / 100, area_en_boleta: Math.round(v.areaBol * 100) / 100 });
    // Lote sin NINGUNA boleta y con al menos un bloque de fecha sospechosa → señal real
    // (un lote sin boleta porque su ciclo no toca esta semana NO se marca).
    if (v.enBoleta === 0 && v.sospechoso) {
      lotes_sin_boleta.push({ lote_num, finca: v.finca, bloques_activos: v.activos, estadios: [...v.estadios].join(',') });
    }
  }
  resumen_lotes.sort((a, b) => legacyLoteCompare(a.lote_num, b.lote_num));
  lotes_sin_boleta.sort((a, b) => legacyLoteCompare(a.lote_num, b.lote_num));

  return { lotes_sin_boleta, bloques_excluidos, fechas_sospechosas, resumen_lotes };
}

// ─── Página Principal ─────────────────────────────────────────────────────────

export function BoletasAplicacionPage() {
  const empresaId = useEmpresaId();

  const [fechaDesde, setFechaDesde]   = useState(() => fechaLocalISO());
  const [fechaHasta, setFechaHasta]   = useState(() => sumar6Dias(fechaLocalISO()));
  const [semana, setSemana]           = useState(() => semanaDesdeISO(fechaLocalISO()));
  const [boletas, setBoletas]         = useState<AppBoleta[]>([]);
  const [productos, setProductos]     = useState<AppBoletaProducto[]>([]);
  const [booms, setBooms]             = useState<AppBoom[]>([]);
  const [boletaSel, setBoletaSel]     = useState<AppBoleta | null>(null);
  // Acordeón: id de la boleta expandida (solo una a la vez)
  const [expandidoId, setExpandidoId] = useState<number | null>(null);
  // Cache de productos por boleta para no recargar al abrir/cerrar
  const [productosCache, setProductosCache] = useState<Record<number, AppBoletaProducto[]>>({});
  const [loadingExpandido, setLoadingExpandido] = useState<number | null>(null);
  const [loading, setLoading]         = useState(false);
  const [generando, setGenerando]     = useState(false);
  // Reporte de validación de la última generación (lotes/bloques sin boleta, etc.)
  const [validacion, setValidacion]   = useState<ValidacionGen | null>(null);
  const [progreso, setProgreso]       = useState<ProgresoGen | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [descartando, setDescartando] = useState(false);
  const [imprimiendo, setImprimiendo] = useState(false);
  const [toast, setToast]             = useState<{ msg: string; ok: boolean } | null>(null);
  const [empresaNombre, setEmpresaNombre] = useState('');
  const [empresaCedula, setEmpresaCedula] = useState('');
  const [empresaLogo, setEmpresaLogo]     = useState('');
  const [boomSelId, setBoomSelId]     = useState<number | null>(null);

  const hayBorradores = boletas.some(b => b.estado === 'borrador');

  const resetFechasHoy = useCallback(() => {
    const hoy = fechaLocalISO();
    setFechaDesde(hoy);
    setFechaHasta(sumar6Dias(hoy));
    setSemana(semanaDesdeISO(hoy));
  }, []);

  // Inyectar CSS responsive (tablet y móvil)
  useEffect(() => {
    const id = 'boletas-aplic-responsive';
    if (document.getElementById(id)) return;
    const s = document.createElement('style');
    s.id = id;
    s.textContent = `
      /* Tablet (<1024px): reducir padding, ajustar header */
      @media (max-width: 1024px) {
        .bla-wrap { overflow-x: hidden; }
        .bla-header {
          padding: 10px 12px !important;
          gap: 12px !important;
        }
        .bla-header .bla-totales { gap: 14px !important; }
        .bla-acciones { width: 100%; margin-left: 0 !important; }
        .bla-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        .bla-table-wrap table { min-width: 900px; }
      }
      /* Móvil (<720px): stack vertical y rows simplificados */
      @media (max-width: 720px) {
        .bla-header {
          flex-direction: column;
          align-items: stretch !important;
          gap: 10px !important;
        }
        .bla-header > * { width: 100%; min-width: 0; box-sizing: border-box; }
        /* Bloque DEL/AL/SEMANA: que los inputs no se desborden */
        .bla-semana {
          width: 100%;
          gap: 10px !important;
        }
        .bla-semana > div { flex: 1 1 calc(50% - 5px); min-width: 0; }
        .bla-semana input[type="date"] {
          width: 100% !important;
          min-width: 0;
          box-sizing: border-box;
        }
        .bla-totales {
          display: grid !important;
          grid-template-columns: repeat(2, 1fr);
          gap: 8px !important;
          align-items: start !important;
        }
        .bla-acciones {
          flex-wrap: wrap;
          gap: 8px !important;
          margin-left: 0 !important;
        }
        .bla-acciones > button { flex: 1 1 calc(50% - 4px); min-width: 0; }
        .bla-acciones > div { flex: 1 1 100%; min-width: 0; }
        .bla-acciones select { width: 100% !important; }
        /* Fila expandida: el detalle a ancho completo */
        .bla-table-wrap td > div[style*="border-left"] { padding-left: 12px !important; }
        .bla-table-wrap td > div > div:first-child { padding-left: 12px !important; }
      }
      /* Pantallas muy chicas (<420px): el bloque Semana va a su propia fila */
      @media (max-width: 420px) {
        .bla-semana > div:nth-child(3) {
          flex: 1 1 100%;
          align-items: flex-start !important;
        }
      }
    `;
    document.head.appendChild(s);
  }, []);

  const [sortCol, setSortCol]   = useState<string>('numero');
  const [sortDir, setSortDir]   = useState<'asc' | 'desc'>('asc');

  const COLS: { key: string; label: string; num?: boolean }[] = [
    { key: 'numero',       label: 'No. Boleta' },
    { key: 'estado',       label: 'Estado' },
    { key: 'lote_num',     label: 'Lote' },
    { key: 'finca_nombre', label: 'Finca' },
    { key: 'fecha_siembra',label: 'Siembra' },
    { key: 'fecha_plan',   label: 'F. Plan' },
    { key: 'fecha_aplicacion', label: 'F. Aplicar' },
    { key: 'boom_capacidad_l', label: 'Boom',   num: true },
    { key: 'agua_l',       label: 'Agua',    num: true },
    { key: 'plantas',      label: 'Plantas', num: true },
    { key: 'area_ha',      label: 'Área',    num: true },
    { key: 'etapa_nombre', label: 'Plan' },
    { key: 'ciclo_nombre', label: 'Ciclo' },
    { key: 'dias',         label: 'Días',    num: true },
    { key: 'bloques',      label: 'Bloques' },
  ];

  function toggleSort(col: string) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  const boletasOrdenadas = [...boletas].sort((a, b) => {
    const col = COLS.find(c => c.key === sortCol);
    const av = (a as any)[sortCol] ?? '';
    const bv = (b as any)[sortCol] ?? '';
    const cmp = col?.num
      ? Number(av) - Number(bv)
      : String(av).localeCompare(String(bv), 'es-CR');
    return sortDir === 'asc' ? cmp : -cmp;
  });

  // Totales del header
  const totalPlantas  = boletas.reduce((s, b) => s + (b.plantas ?? 0), 0);
  const totalArea     = boletas.reduce((s, b) => s + Number(b.area_ha ?? 0), 0);
  const densMedia     = totalArea > 0 ? totalPlantas / totalArea : 0;

  // Cuando cambia fecha desde → calcular hasta (+6) y semana
  // La semana ISO se calcula desde el lunes siguiente al domingo de inicio
  // porque el VFP considera que el domingo de inicio pertenece a la semana que empieza ese lunes
  useEffect(() => {
    if (!fechaDesde) return;
    const d = new Date(fechaDesde + 'T00:00:00');
    const hasta = sumar6Dias(fechaDesde);
    setFechaHasta(hasta);
    setValidacion(null);  // el panel de validación es de la última generación; al cambiar de semana ya no aplica
    // Si es domingo (getDay()===0), usar el lunes siguiente para el número de semana
    const dParaSemana = new Date(d);
    if (dParaSemana.getDay() === 0) dParaSemana.setDate(dParaSemana.getDate() + 1);
    setSemana(semanaLabel(dParaSemana));
  }, [fechaDesde]);

  // Cuando cambia la boleta seleccionada → cargar sus productos
  useEffect(() => {
    if (!boletaSel) { setProductos([]); return; }
    supabase
      .from('app_boleta_productos')
      .select('*')
      .eq('boleta_id', boletaSel.id)
      .order('es_agua')
      .order('orden')
      .then(({ data }) => setProductos((data ?? []) as AppBoletaProducto[]));
  }, [boletaSel]);

  // Cargar productos del acordeón bajo demanda (con cache)
  useEffect(() => {
    if (expandidoId == null) return;
    if (productosCache[expandidoId]) return;
    setLoadingExpandido(expandidoId);
    supabase
      .from('app_boleta_productos')
      .select('*')
      .eq('boleta_id', expandidoId)
      .order('es_agua')
      .order('orden')
      .then(({ data }) => {
        setProductosCache((prev) => ({ ...prev, [expandidoId]: (data ?? []) as AppBoletaProducto[] }));
        setLoadingExpandido((curr) => (curr === expandidoId ? null : curr));
      });
  }, [expandidoId, productosCache]);

  function toggleExpandido(id: number) {
    // El detalle es una foto cacheada. Al volver a abrirlo se invalida para
    // reflejar enriquecimientos de catálogo (ART01.CATALOGO → justificación)
    // o correcciones hechas después de generar la cédula.
    setProductosCache((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setExpandidoId((curr) => (curr === id ? null : id));
  }

  // Al montar: cargar booms, empresa y verificar si hay borradores pendientes
  useEffect(() => {
    supabase.from('app_booms').select('*').eq('empresa_id', empresaId).eq('activo', true)
      .then(({ data }) => {
        const lista = (data ?? []) as AppBoom[];
        setBooms(lista);
        // Preseleccionar el boom de 6000 L por defecto (el más usado), si aún no hay uno elegido
        setBoomSelId(prev => prev ?? (lista.find(b => Number(b.capacidad_l) === 6000)?.id ?? null));
      });
    Promise.all([
      supabase.from('empresas').select('nombre, cedula').eq('id', empresaId).maybeSingle(),
      supabase.from('fe_config_empresa').select('logo_url').eq('empresa_id', empresaId).maybeSingle(),
    ]).then(([{ data: emp }, { data: cfg }]) => {
      if (emp) {
        setEmpresaNombre((emp as any).nombre ?? '');
        setEmpresaCedula((emp as any).cedula ?? '');
      }
      setEmpresaLogo((cfg as any)?.logo_url ?? '');
    });
    // Si hay borradores sin confirmar, cargarlos automáticamente
    supabase
      .from('app_boletas')
      .select('fecha_desde, fecha_hasta')
      .eq('empresa_id', empresaId)
      .eq('estado', 'borrador')
      .order('fecha_desde')
      .limit(1)
      .then(({ data }) => {
        if (data && data.length > 0) {
          setFechaDesde(data[0].fecha_desde);
          // fecha_hasta y semana se actualizan por el useEffect de fechaDesde
          // consultar() se dispara desde el useEffect de fechaDesde/fechaHasta abajo
        }
      });
  }, [empresaId]);

  const fetchProductosBoleta = async (boletaId: number): Promise<AppBoletaProducto[]> => {
    const { data } = await supabase
      .from('app_boleta_productos')
      .select('*')
      .eq('boleta_id', boletaId)
      .order('es_agua')
      .order('orden');
    return (data ?? []) as AppBoletaProducto[];
  };

  // ── Reportes de insumos ────────────────────────────────────────────────────

  const handleReporteInsumos = async () => {
    if (boletas.length === 0) return;
    const boletaIds = boletas.map(b => b.id).filter(Boolean);
    const { data: prods } = await supabase
      .from('app_boleta_productos')
      .select('articulo, nombre_producto, medida, cantidad_aplicar, es_agua')
      .in('boleta_id', boletaIds)
      .eq('empresa_id', empresaId)
      .eq('excluida', false);
    if (!prods) return;

    const map = new Map<string, { nombre: string; medida: string; cantidad: number; esAgua: boolean }>();
    for (const p of prods) {
      const key = p.articulo;
      if (!map.has(key)) map.set(key, { nombre: p.nombre_producto, medida: p.medida ?? '', cantidad: 0, esAgua: !!p.es_agua });
      map.get(key)!.cantidad += Number(p.cantidad_aplicar ?? 0);
    }
    const filas = [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    const aguaTotal = filas.reduce((s, [, v]) => s + (v.esAgua ? v.cantidad : 0), 0);
    const totalProductos = filas.filter(([, v]) => !v.esAgua).length;
    const semLabel = semana || `${fechaDesde} / ${fechaHasta}`;
    const fmt = (n: number) => n.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const empresa = empresaNombre || 'AGROPECUARIA VASQUEZ Y ZUÑIGA, S.A.';
    const logoHtml = empresaLogo ? `<img src="${empresaLogo}" alt="Logo" style="width:54px;height:54px;object-fit:contain;">` : '';
    const ahora = new Date().toLocaleDateString('es-CR', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });

    const filasBg = filas.map(([cod, v], i) =>
      `<tr class="${v.esAgua ? 'agua' : i % 2 === 0 ? '' : 'alt'}">
        <td class="cod">${cod}</td>
        <td class="nombre">${v.nombre}</td>
        <td class="med">${v.medida}</td>
        <td class="num">${fmt(v.cantidad)}</td>
        <td class="linea"></td>
      </tr>`
    ).join('');

    const html = `<!DOCTYPE html>
<html lang="es"><head><meta charset="utf-8">
<title>Lista de Insumos — Semana ${semLabel}</title>
<style>
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 12px; color: #1a1a2e; background: #fff; padding: 28px 32px; }

  /* ── Encabezado ── */
  .header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 3px solid #1b5e20; gap: 16px; }
  .header-logo { width: 64px; min-width: 64px; text-align: center; }
  .header-left { flex: 1; }
  .empresa { font-size: 17px; font-weight: 700; color: #1b5e20; letter-spacing: .4px; }
  .modulo  { font-size: 11px; font-weight: 600; color: #558b2f; text-transform: uppercase; letter-spacing: 1px; margin-top: 2px; }
  .header-right { text-align: right; }
  .titulo-doc { font-size: 20px; font-weight: 700; color: #1a1a2e; line-height: 1.1; }
  .semana-badge { display: inline-block; margin-top: 6px; background: #1b5e20; color: #fff; font-size: 13px; font-weight: 700; padding: 3px 14px; border-radius: 20px; letter-spacing: .5px; }
  .fecha-gen { font-size: 9px; color: #888; margin-top: 6px; }

  /* ── Tabla ── */
  table { width: 100%; border-collapse: collapse; margin-top: 4px; }
  thead { display: table-header-group; }
  thead tr { background: #1b5e20; }
  thead th { color: #fff; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: .8px; padding: 9px 10px; text-align: left; }
  thead th.r { text-align: right; }
  tbody tr { border-bottom: 1px solid #e8f5e9; }
  tbody tr.alt { background: #f1f8e9; }
  tbody tr.agua { background: #e3f2fd; }
  tbody tr:hover { background: #fffde7; }
  tbody tr { break-inside: avoid; page-break-inside: avoid; }
  td { padding: 7px 10px; font-size: 11.5px; vertical-align: middle; }
  td.cod   { font-weight: 600; color: #2e7d32; width: 80px; font-size: 11px; }
  td.nombre { color: #1a1a2e; }
  td.med   { color: #666; font-size: 10.5px; width: 60px; }
  td.num   { text-align: right; font-weight: 600; color: #1a1a2e; width: 110px; font-variant-numeric: tabular-nums; }
  td.linea { width: 120px; border-bottom: 1px solid #bdbdbd; vertical-align: bottom; padding-bottom: 2px; }

  /* ── Totales ── */
  tfoot tr { background: #1b5e20; }
  tfoot { break-inside: avoid; page-break-inside: avoid; }
  tfoot td { color: #fff; font-weight: 700; font-size: 11px; padding: 8px 10px; }
  tfoot td.num { text-align: right; }

  /* ── Footer ── */
  .footer { margin-top: 20px; display: flex; justify-content: space-between; align-items: center; font-size: 9.5px; color: #888; border-top: 1px solid #e0e0e0; padding-top: 10px; }
  .firma { display: flex; gap: 48px; }
  .firma-item { text-align: center; }
  .firma-linea { width: 120px; border-top: 1px solid #555; margin-bottom: 3px; }
  .firma-label { font-size: 9px; color: #666; }

  @media print {
    body { padding: 0; }
    @page { margin: 14mm 12mm; size: letter portrait; }
    tbody tr:hover { background: inherit; }
    .header, .footer { break-inside: avoid; page-break-inside: avoid; }
  }
</style>
</head><body>

<div class="header">
  <div class="header-logo">
    ${empresaLogo
      ? `<img src="${empresaLogo}" alt="Logo" style="width:58px;height:58px;object-fit:contain;">`
      : `<div style="width:58px;height:58px;"></div>`
    }
  </div>
  <div class="header-left">
    <div class="empresa">${empresa}</div>
    <div class="modulo">Programa de Aplicaciones en Campo</div>
  </div>
  <div class="header-right">
    <div class="titulo-doc">Lista de Insumos</div>
    <div><span class="semana-badge">Semana ${semLabel}</span></div>
    <div class="fecha-gen">Generado: ${ahora}</div>
  </div>
</div>

<table>
  <thead>
    <tr>
      <th style="width:80px">Código</th>
      <th>Artículo</th>
      <th style="width:60px">Medida</th>
      <th class="r" style="width:110px">Cantidad</th>
      <th style="width:130px">V°B°</th>
    </tr>
  </thead>
  <tbody>${filasBg}</tbody>
  <tfoot>
    <tr>
      <td colspan="3">Total productos: ${totalProductos} &nbsp;•&nbsp; Agua total a utilizar</td>
      <td class="num">${fmt(aguaTotal)} LIT</td>
      <td></td>
    </tr>
  </tfoot>
</table>

<div class="footer">
  <div class="firma">
    <div class="firma-item">
      <div class="firma-linea"></div>
      <div class="firma-label">Preparado por</div>
    </div>
    <div class="firma-item">
      <div class="firma-linea"></div>
      <div class="firma-label">Revisado por</div>
    </div>
    <div class="firma-item">
      <div class="firma-linea"></div>
      <div class="firma-label">Autorizado por</div>
    </div>
  </div>
  <div>${empresa} &bull; ${ahora}</div>
</div>

</body></html>`;

    const ifr = document.createElement('iframe');
    ifr.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:none;';
    document.body.appendChild(ifr);
    const d = ifr.contentDocument || ifr.contentWindow?.document;
    if (d) { d.open(); d.write(html); d.close(); }
    setTimeout(() => { if (ifr.contentDocument) ifr.contentDocument.title = `Lista de Insumos - Semana ${semLabel}`; ifr.contentWindow?.focus(); ifr.contentWindow?.print(); setTimeout(() => document.body.removeChild(ifr), 1000); }, 500);
  };

  const handleReporteInventario = async () => {
    if (boletas.length === 0) return;
    const boletaIds = boletas.map(b => b.id).filter(Boolean);
    const { data: prods } = await supabase
      .from('app_boleta_productos')
      .select('articulo, nombre_producto, medida, cantidad_aplicar, es_agua')
      .in('boleta_id', boletaIds)
      .eq('empresa_id', empresaId)
      .eq('es_agua', false)
      .eq('excluida', false);
    if (!prods) return;

    const map = new Map<string, { nombre: string; medida: string; requerido: number }>();
    for (const p of prods) {
      const key = p.articulo;
      if (!map.has(key)) map.set(key, { nombre: p.nombre_producto, medida: p.medida ?? '', requerido: 0 });
      map.get(key)!.requerido += Number(p.cantidad_aplicar ?? 0);
    }

    const codigos = [...map.keys()];
    const { data: invProds } = await supabase
      .from('inv_productos')
      .select('codigo, stock_actual, unidad_medida')
      .eq('empresa_id', empresaId)
      .in('codigo', codigos);

    const saldoMap = new Map<string, { saldo: number; medidaInv: string }>();
    for (const ip of (invProds ?? [])) {
      saldoMap.set(ip.codigo, { saldo: Number(ip.stock_actual ?? 0), medidaInv: ip.unidad_medida ?? '' });
    }

    const filas = [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
    const semLabel = semana || `${fechaDesde} / ${fechaHasta}`;
    const fmt = (n: number) => n.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const empresa = empresaNombre || 'AGROPECUARIA VASQUEZ Y ZUÑIGA, S.A.';
    const logoHtml = empresaLogo ? `<img src="${empresaLogo}" alt="Logo" style="width:54px;height:54px;object-fit:contain;">` : '';
    const ahora = new Date().toLocaleDateString('es-CR', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' });

    const totalReq = filas.reduce((s, [, v]) => s + v.requerido, 0);
    const totalFalt = filas.reduce((s, [cod, v]) => {
      const saldo = saldoMap.get(cod)?.saldo ?? 0;
      const f = v.requerido - saldo;
      return s + (f > 0 ? f : 0);
    }, 0);
    const conFaltante = filas.filter(([cod, v]) => (v.requerido - (saldoMap.get(cod)?.saldo ?? 0)) > 0).length;

    const filasBg = filas.map(([cod, v], i) => {
      const inv = saldoMap.get(cod);
      const saldo = inv?.saldo ?? 0;
      const faltante = v.requerido - saldo;
      const empaque = v.medida || inv?.medidaInv || '';
      const rowClass = faltante > 0 ? 'falt-row' : i % 2 === 0 ? '' : 'alt';
      return `<tr class="${rowClass}">
        <td class="cod">${cod}</td>
        <td class="nombre">${v.nombre}</td>
        <td class="emp">${empaque}</td>
        <td class="num saldo">${saldo > 0 ? fmt(saldo) : '<span class="cero">—</span>'}</td>
        <td class="num req">${fmt(v.requerido)}</td>
        <td class="num ${faltante > 0 ? 'falt' : 'ok'}">${faltante > 0 ? fmt(faltante) : '<span class="check">✓</span>'}</td>
      </tr>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html lang="es"><head><meta charset="utf-8">
<title>Control de Inventario — Semana ${semLabel}</title>
<style>
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 12px; color: #1a1a2e; background: #fff; padding: 28px 32px; }

  /* ── Encabezado ── */
  .header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; padding-bottom: 16px; border-bottom: 3px solid #1565c0; gap: 16px; }
  .header-logo { width: 64px; min-width: 64px; text-align: center; }
  .header-center { flex: 1; }
  .empresa { font-size: 17px; font-weight: 700; color: #1565c0; letter-spacing: .4px; }
  .modulo  { font-size: 11px; font-weight: 600; color: #1976d2; text-transform: uppercase; letter-spacing: 1px; margin-top: 2px; }
  .header-right { text-align: right; }
  .titulo-doc { font-size: 20px; font-weight: 700; color: #1a1a2e; line-height: 1.1; }
  .semana-badge { display: inline-block; margin-top: 6px; background: #1565c0; color: #fff; font-size: 13px; font-weight: 700; padding: 3px 14px; border-radius: 20px; }
  .fecha-gen { font-size: 9px; color: #888; margin-top: 6px; }

  /* ── KPIs ── */
  .kpis { display: flex; gap: 12px; margin-bottom: 18px; }
  .kpi { flex: 1; border-radius: 8px; padding: 10px 14px; }
  .kpi.total  { background: #e3f2fd; border: 1px solid #90caf9; }
  .kpi.falt   { background: #ffebee; border: 1px solid #ef9a9a; }
  .kpi.ok     { background: #e8f5e9; border: 1px solid #a5d6a7; }
  .kpi-val  { font-size: 22px; font-weight: 700; line-height: 1; }
  .kpi.total .kpi-val { color: #1565c0; }
  .kpi.falt  .kpi-val { color: #c62828; }
  .kpi.ok    .kpi-val { color: #2e7d32; }
  .kpi-label { font-size: 10px; color: #555; margin-top: 3px; text-transform: uppercase; letter-spacing: .5px; }

  /* ── Tabla ── */
  table { width: 100%; border-collapse: collapse; }
  thead tr { background: #1565c0; }
  thead th { color: #fff; font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: .8px; padding: 9px 10px; text-align: left; }
  thead th.r { text-align: right; }
  tbody tr { border-bottom: 1px solid #e3f2fd; }
  tbody tr.alt { background: #f8fbff; }
  tbody tr.falt-row { background: #fff8f8; }
  td { padding: 6px 10px; font-size: 11.5px; vertical-align: middle; }
  td.cod   { font-weight: 600; color: #1565c0; width: 72px; font-size: 11px; }
  td.nombre { color: #1a1a2e; }
  td.emp   { color: #666; font-size: 10.5px; width: 56px; }
  td.num   { text-align: right; font-variant-numeric: tabular-nums; width: 100px; }
  td.saldo { color: #555; }
  td.req   { font-weight: 600; color: #1a1a2e; }
  td.falt  { font-weight: 700; color: #c62828; }
  td.ok    { color: #2e7d32; font-size: 14px; text-align: center; }
  .cero    { color: #bbb; }
  .check   { color: #2e7d32; }

  /* ── Totales ── */
  tfoot tr { background: #1565c0; }
  tfoot td { color: #fff; font-weight: 700; font-size: 11px; padding: 8px 10px; }
  tfoot td.num { text-align: right; font-variant-numeric: tabular-nums; }
  tfoot td.falt-tot { color: #ffcdd2; text-align: right; }

  /* ── Footer ── */
  .footer { margin-top: 20px; display: flex; justify-content: space-between; align-items: center; font-size: 9.5px; color: #888; border-top: 1px solid #e0e0e0; padding-top: 10px; }

  @media print {
    body { padding: 0; }
    @page { margin: 14mm 12mm; size: letter portrait; }
    tbody tr.falt-row { background: #fff8f8 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    thead tr, tfoot tr { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head><body>

<div class="header">
  <div class="header-logo">
    ${empresaLogo
      ? `<img src="${empresaLogo}" alt="Logo" style="width:58px;height:58px;object-fit:contain;">`
      : `<div style="width:58px;height:58px;"></div>`
    }
  </div>
  <div class="header-center">
    <div class="empresa">${empresa}</div>
    <div class="modulo">Programación de Aplicaciones en Campo</div>
  </div>
  <div class="header-right">
    <div class="titulo-doc">Control de Inventario</div>
    <div><span class="semana-badge">Semana ${semLabel}</span></div>
    <div class="fecha-gen">Generado: ${ahora}</div>
  </div>
</div>

<div class="kpis">
  <div class="kpi total">
    <div class="kpi-val">${filas.length}</div>
    <div class="kpi-label">Productos requeridos</div>
  </div>
  <div class="kpi falt">
    <div class="kpi-val">${conFaltante}</div>
    <div class="kpi-label">Con faltante</div>
  </div>
  <div class="kpi ok">
    <div class="kpi-val">${filas.length - conFaltante}</div>
    <div class="kpi-label">Cubiertos</div>
  </div>
</div>

<table>
  <thead>
    <tr>
      <th style="width:72px">Código</th>
      <th>Artículo</th>
      <th style="width:56px">Emp.</th>
      <th class="r" style="width:100px">Saldo</th>
      <th class="r" style="width:100px">Requerido</th>
      <th class="r" style="width:100px">Faltante</th>
    </tr>
  </thead>
  <tbody>${filasBg}</tbody>
  <tfoot>
    <tr>
      <td colspan="3">Total: ${filas.length} productos &bull; ${conFaltante} con faltante</td>
      <td class="num">&nbsp;</td>
      <td class="num">${fmt(totalReq)}</td>
      <td class="falt-tot">${totalFalt > 0 ? fmt(totalFalt) : '✓'}</td>
    </tr>
  </tfoot>
</table>

<div class="footer">
  <span>${empresa} &bull; Semana ${semLabel}</span>
  <span>${ahora}</span>
</div>

</body></html>`;

    const ifr = document.createElement('iframe');
    ifr.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:none;';
    document.body.appendChild(ifr);
    const d = ifr.contentDocument || ifr.contentWindow?.document;
    if (d) { d.open(); d.write(html); d.close(); }
    setTimeout(() => { if (ifr.contentDocument) ifr.contentDocument.title = `Control de Inventario - Semana ${semLabel}`; ifr.contentWindow?.focus(); ifr.contentWindow?.print(); setTimeout(() => document.body.removeChild(ifr), 1000); }, 500);
  };

  const handleImprimirTodas = async () => {
    if (boletas.length === 0) return;
    setImprimiendo(true);
    try {
      await imprimirBoletas(boletas, fetchProductosBoleta, empresaNombre, empresaCedula, empresaLogo);
    } finally {
      setImprimiendo(false);
    }
  };

  const handleImprimirUna = async () => {
    if (!boletaSel) return;
    setImprimiendo(true);
    try {
      await imprimirBoletas([boletaSel], fetchProductosBoleta, empresaNombre, empresaCedula, empresaLogo);
    } finally {
      setImprimiendo(false);
    }
  };

  // manual=true → consulta pedida por el usuario (botón Consultar): muestra avisos.
  // manual=false → auto-consulta al montar/cambiar semana: NO molesta con toasts.
  const consultar = useCallback(async (manual = false) => {
    if (!fechaDesde || !fechaHasta) return;
    setLoading(true);
    // Solo mostrar borradores — las confirmadas (pendiente/impresa/aplicada) viven en Cédulas
    const { data, error } = await supabase
      .from('app_boletas')
      .select('*')
      .eq('empresa_id', empresaId)
      .eq('fecha_desde', fechaDesde)
      .eq('fecha_hasta', fechaHasta)
      .eq('manual', false)
      .eq('estado', 'borrador')
      .order('numero');
    setLoading(false);
    if (error) { setToast({ msg: error.message, ok: false }); return; }
    if (manual && (!data || data.length === 0)) {
      // Solo en consulta manual: avisar si la semana ya está confirmada
      const { data: exist } = await supabase
        .from('app_boletas')
        .select('id')
        .eq('empresa_id', empresaId)
        .eq('fecha_desde', fechaDesde)
        .eq('manual', false)
        .in('estado', ['pendiente', 'impresa', 'aplicada'])
        .limit(1);
      if (exist && exist.length > 0) {
        setToast({ msg: `La semana ${semana || fechaDesde} ya fue confirmada. Consultá las boletas en Cédulas (Historial).`, ok: false });
      }
    }
    const nextBoletas = (data ?? []) as AppBoleta[];
    setBoletas(nextBoletas);
    setBoletaSel(prev => {
      if (!prev) return null;
      return nextBoletas.find(b => b.id === prev.id) ?? null;
    });
  }, [empresaId, fechaDesde, fechaHasta, semana]);

  // Auto-consultar cuando fechaDesde/fechaHasta cambian (también al montar si hay borradores)
  useEffect(() => {
    if (fechaDesde && fechaHasta) consultar();
  }, [fechaDesde, fechaHasta, consultar]);

  const handleGenerar = async () => {
    if (!fechaDesde || !fechaHasta) {
      setToast({ msg: 'Ingresá la fecha de inicio de semana', ok: false });
      return;
    }
    // Bloquear si hay borradores pendientes de confirmar o descartar
    if (hayBorradores) {
      setToast({ msg: 'Hay borradores sin confirmar. Confirmá o descartá la semana actual antes de generar otra.', ok: false });
      return;
    }
    // Verificar duplicados: por semana ISO o por rango exacto de fechas
    const { data: exist } = await supabase
      .from('app_boletas')
      .select('id')
      .eq('empresa_id', empresaId)
      .eq('semana', semana)
      .eq('manual', false)
      .neq('estado', 'cancelada')
      .limit(1);
    if (exist && exist.length > 0) {
      setToast({ msg: `Ya existen boletas para la semana ${semana}. Use Consultar para verlas.`, ok: false });
      return;
    }
    setGenerando(true);
    setProgreso(null);
    const reportar = (p: ProgresoGen) => {
      setProgreso(p);
      return new Promise<void>(res => setTimeout(res, 0));
    };
    const boomObj = boomSelId ? booms.find(b => b.id === boomSelId) ?? null : null;
    const result = await generarBoletas(empresaId, fechaDesde, fechaHasta, semana, boomObj ? { id: boomObj.id, nombre: boomObj.nombre, capacidad_l: boomObj.capacidad_l } : null, reportar);
    setGenerando(false);
    setProgreso(null);
    if (!result.ok) {
      setToast({ msg: result.error ?? 'Error desconocido', ok: false });
      return;
    }
    setValidacion(result.validacion ?? null);
    if (result.count === 0) {
      setToast({ msg: result.debug ?? 'No se encontraron aplicaciones para esta semana según el plan de manejo', ok: false });
      return;
    }
    const v = result.validacion;
    const alertas = v ? (v.lotes_sin_boleta.length + v.bloques_excluidos.length + v.fechas_sospechosas.length) : 0;
    if (alertas > 0) {
      setToast({ msg: `${result.count} boletas generadas — ⚠️ ${alertas} alerta${alertas > 1 ? 's' : ''} de validación, revisá el panel antes de confirmar`, ok: false });
    } else {
      setToast({ msg: `${result.count} boleta${result.count > 1 ? 's' : ''} generada${result.count > 1 ? 's' : ''} — validación OK`, ok: true });
    }
    consultar();
  };

  // Confirmar borradores → pasan a pendiente (entran al histórico)
  const handleConfirmar = async () => {
    const ids = boletas.filter(b => b.estado === 'borrador').map(b => b.id);
    if (ids.length === 0) return;
    setConfirmando(true);
    const { error } = await supabase
      .from('app_boletas')
      .update({ estado: 'pendiente' })
      .in('id', ids);
    setConfirmando(false);
    if (error) { setToast({ msg: error.message, ok: false }); return; }
    // Limpiar vista — las boletas confirmadas van a Cédulas, no se muestran aquí
    setBoletas([]); setBoletaSel(null); setProductos([]); setValidacion(null);
    resetFechasHoy();
    setToast({ msg: `${ids.length} boleta${ids.length > 1 ? 's' : ''} confirmada${ids.length > 1 ? 's' : ''} y enviadas a Cédulas`, ok: true });
  };

  // Descartar borradores → borrar boletas + productos + resetear consecutivo
  const handleDescartar = async () => {
    const ids = boletas.filter(b => b.estado === 'borrador').map(b => b.id);
    if (ids.length === 0) return;
    setDescartando(true);
    const { error: errProd } = await supabase.from('app_boleta_productos').delete().in('boleta_id', ids);
    if (errProd) { setDescartando(false); setToast({ msg: `Error productos: ${errProd.message}`, ok: false }); return; }
    const { error } = await supabase.from('app_boletas').delete().in('id', ids);
    if (error) { setDescartando(false); setToast({ msg: error.message, ok: false }); return; }
    // Revertir consecutivo al último número confirmado
    await supabase.rpc('app_revertir_boletas_seq', { p_empresa_id: empresaId });
    setDescartando(false);
    setToast({ msg: `${ids.length} borrador${ids.length > 1 ? 'es' : ''} descartado${ids.length > 1 ? 's' : ''}`, ok: false });
    setBoletas([]); setBoletaSel(null); setProductos([]); setValidacion(null);
    resetFechasHoy();
  };

  const productosSel = boletaSel ? productos : [];
  const progresoPct = progreso
    ? Math.min(100, Math.max(progreso.paso === 0 ? 3 : 0, Math.round((progreso.paso / Math.max(progreso.total, 1)) * 100)))
    : 0;
  const progresoConteo = progreso
    ? progreso.total > 1
      ? `${progreso.paso} de ${progreso.total}`
      : 'Preparando'
    : '';

  return (
    <div className="bla-wrap flex flex-col h-full overflow-hidden">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="bla-header px-5 py-3 shrink-0 flex items-start gap-6 flex-wrap"
        style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-raised)' }}>

        {/* Título */}
        <div className="shrink-0">
          <h1 className={pageTitleCls}>Boletas de Aplicación</h1>
          <p className={pageSubtitleCls}>Generación semanal desde el Plan de Manejo</p>
        </div>

        {/* Semana */}
        <div className="bla-semana flex items-end gap-3 flex-wrap">
          <div>
            <label className={labelCls}>Del</label>
            <div className="flex items-center gap-2">
              <input type="date" className={inputCls} style={{ width: '9rem' }}
                value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
              {fechaDesde && (
                <span className="text-xs capitalize" style={{ color: 'var(--ink-muted)' }}>
                  {diaSemana(fechaDesde)}
                </span>
              )}
            </div>
          </div>
          <div>
            <label className={labelCls}>Al</label>
            <div className="flex items-center gap-2">
              <input type="date" className={inputCls}
                value={fechaHasta} readOnly
                style={{ width: '9rem', opacity: 0.7, cursor: 'not-allowed' }} />
              {fechaHasta && (
                <span className="text-xs capitalize" style={{ color: 'var(--ink-muted)' }}>
                  {diaSemana(fechaHasta)}
                </span>
              )}
            </div>
          </div>
          {semana && (
            <div className="flex flex-col items-center">
              <span className="text-[9px] uppercase tracking-widest" style={{ color: 'var(--ink-faint)' }}>Semana</span>
              <span className="text-base font-bold font-mono" style={{ color: 'var(--emp-accent-txt)' }}>{semana}</span>
            </div>
          )}
        </div>

        {/* Totales */}
        {boletas.length > 0 && (
          <div className="bla-totales flex items-end gap-5 text-xs">
            <div>
              <p className="text-[9px] uppercase tracking-widest mb-0.5" style={{ color: 'var(--ink-faint)' }}>Plantas</p>
              <p className="font-bold font-mono text-sm" style={{ color: 'var(--ink)' }}>{totalPlantas.toLocaleString('es-CR')}</p>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-widest mb-0.5" style={{ color: 'var(--ink-faint)' }}>Área total</p>
              <p className="font-bold font-mono text-sm" style={{ color: 'var(--ink)' }}>{fmtN(totalArea)} ha</p>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-widest mb-0.5" style={{ color: 'var(--ink-faint)' }}>Densidad</p>
              <p className="font-bold font-mono text-sm" style={{ color: 'var(--ink)' }}>{fmtN(densMedia, 0)}</p>
            </div>
            {boletas[0]?.boom_nombre && (
              <div>
                <p className="text-[9px] uppercase tracking-widest mb-0.5" style={{ color: 'var(--ink-faint)' }}>Boom</p>
                <p className="font-mono text-sm" style={{ color: 'var(--ink-muted)' }}>{boletas[0].boom_nombre} — {boletas[0].boom_capacidad_l?.toLocaleString('es-CR')} L</p>
              </div>
            )}
          </div>
        )}

        {/* Botones */}
        <div className="bla-acciones ml-auto flex items-end gap-2 shrink-0">
          <button onClick={() => consultar(true)} disabled={loading || !fechaDesde}
            className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium"
            style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
            {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            Consultar
          </button>

          {/* Acciones de borrador — solo visibles cuando hay borradores */}
          {hayBorradores && (
            <>
              <button onClick={handleDescartar} disabled={descartando || confirmando}
                className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-semibold"
                style={{ background: 'var(--tone-warn-bg)', border: '1px solid var(--tone-warn-border)', color: 'var(--tone-warn-text)', opacity: descartando ? 0.6 : 1 }}>
                {descartando ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />}
                {descartando ? 'Descartando…' : 'Descartar'}
              </button>
              <button onClick={handleConfirmar} disabled={confirmando || descartando}
                className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-semibold"
                style={{ background: 'var(--tone-success-bg)', border: '1px solid var(--tone-success-border)', color: 'var(--tone-success-text)', opacity: confirmando ? 0.6 : 1 }}>
                {confirmando ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
                {confirmando ? 'Confirmando…' : `Confirmar ${boletas.filter(b => b.estado === 'borrador').length} boletas`}
              </button>
            </>
          )}

          {/* Generar — deshabilitado visualmente si hay borradores */}
          {!hayBorradores && (
            <>
              <div>
                <label className={labelCls}>Boom a usar</label>
                <select className={inputCls} style={{ width: '10rem' }}
                  value={boomSelId ?? ''}
                  onChange={e => setBoomSelId(e.target.value ? Number(e.target.value) : null)}>
                  <option value="">Sin boom</option>
                  {booms.map(b => (
                    <option key={b.id} value={b.id}>{b.nombre} — {b.capacidad_l.toLocaleString('es-CR')} L</option>
                  ))}
                </select>
              </div>
              <button onClick={handleGenerar} disabled={generando || !fechaDesde}
                className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-semibold"
                style={{ background: 'var(--emp-accent)', color: 'var(--emp-accent-on)', opacity: generando ? 0.6 : 1 }}>
                {generando ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
                {generando ? 'Generando…' : 'Generar semana'}
              </button>
            </>
          )}
          {boletas.length > 0 && (
            <>
              <button onClick={handleReporteInsumos}
                className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium"
                style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
                <FileText size={13} />
                Lista Insumos
              </button>
              <button onClick={handleReporteInventario}
                className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium"
                style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
                <Package size={13} />
                Control Inventario
              </button>
              <button onClick={handleImprimirTodas} disabled={imprimiendo}
                className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium"
                style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)', opacity: imprimiendo ? 0.6 : 1 }}>
                {imprimiendo ? <Loader2 size={13} className="animate-spin" /> : <Printer size={13} />}
                Imprimir todas
              </button>
              {boletaSel && (
                <button onClick={handleImprimirUna} disabled={imprimiendo}
                  className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium"
                  style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)', opacity: imprimiendo ? 0.6 : 1 }}>
                  {imprimiendo ? <Loader2 size={13} className="animate-spin" /> : <Printer size={13} />}
                  Boleta seleccionada
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Banner borrador ─────────────────────────────────────────────────── */}
      {hayBorradores && (
        <div className="px-5 py-2 shrink-0 flex items-center gap-2 text-xs font-semibold"
          style={{ background: '#e8eaf6', borderBottom: '1px solid #9fa8da', color: '#3949ab' }}>
          <AlertCircle size={14} />
          Estas boletas son borradores — aún no están en el historial. Revisá los datos y confirmá para enviarlas al campo, o descartá para corregir y regenerar.
        </div>
      )}

      {/* ── Panel de validación de generación ───────────────────────────────── */}
      {validacion && (() => {
        const v = validacion;
        const nAlertas = v.lotes_sin_boleta.length + v.bloques_excluidos.length + v.fechas_sospechosas.length;
        const ok = nAlertas === 0;
        const lotesConBoleta = v.resumen_lotes.filter(r => r.bloques_en_boleta > 0).length;
        return (
          <div className="px-5 py-2.5 shrink-0 text-xs"
            style={{ background: ok ? 'var(--tone-success-bg)' : '#fff4e5', borderBottom: `1px solid ${ok ? 'var(--tone-success-border)' : '#ffb74d'}`, color: ok ? 'var(--tone-success-text)' : '#a15c00' }}>
            <div className="flex items-center gap-2 font-semibold">
              {ok ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
              {ok
                ? `Validación OK — sin lotes ni bloques con problemas de fecha/estadio (${lotesConBoleta} de ${v.resumen_lotes.length} lotes activos aplicaron esta semana).`
                : `Validación: ${nAlertas} alerta${nAlertas > 1 ? 's' : ''} — revisá antes de confirmar. Corregí el bloque/fecha en Estadios o Fincas/Lotes, descartá y regenerá.`}
            </div>
            {!ok && (
              <div className="mt-1.5 flex flex-col gap-1.5">
                {v.lotes_sin_boleta.length > 0 && (
                  <div><b>Lotes SIN boleta ({v.lotes_sin_boleta.length}):</b>{' '}
                    {v.lotes_sin_boleta.map(l => `Lote ${l.lote_num} (${l.bloques_activos} bloq. ${l.estadios})`).join(' · ')}
                  </div>
                )}
                {v.bloques_excluidos.length > 0 && (
                  <div><b>Bloques activos no incluidos ({v.bloques_excluidos.length}):</b>{' '}
                    {v.bloques_excluidos.slice(0, 20).map(b => `L${b.lote_num}/${b.bloque_num} [${b.estadio}: ${b.motivo}]`).join(' · ')}
                    {v.bloques_excluidos.length > 20 ? ` … +${v.bloques_excluidos.length - 20}` : ''}
                  </div>
                )}
                {v.fechas_sospechosas.length > 0 && (
                  <div><b>Fechas ancla sospechosas ({v.fechas_sospechosas.length}):</b>{' '}
                    {v.fechas_sospechosas.slice(0, 15).map(b => `L${b.lote_num}/${b.bloque_num}: ${b.motivo}`).join(' · ')}
                    {v.fechas_sospechosas.length > 15 ? ` … +${v.fechas_sospechosas.length - 15}` : ''}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Body ────────────────────────────────────────────────────────────── */}
      <div className="flex flex-col flex-1 overflow-auto">

        {/* Barra de progreso generación */}
        {generando && progreso && (
          <div className="px-4 py-3 shrink-0" style={{ borderBottom: '1px solid var(--line)' }}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>{progreso.label}</span>
              <span className="text-xs" style={{ color: 'var(--ink-faint)' }}>{progresoConteo}</span>
            </div>
            <div className="w-full rounded-full overflow-hidden" style={{ height: 6, background: 'var(--surface-deep)' }}>
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{ width: `${progresoPct}%`, background: 'var(--emp-accent)' }}
              />
            </div>
          </div>
        )}
        {generando && !progreso && (
          <div className="px-4 py-3 shrink-0" style={{ borderBottom: '1px solid var(--line)' }}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Iniciando…</span>
            </div>
            <div className="w-full rounded-full overflow-hidden" style={{ height: 6, background: 'var(--surface-deep)' }}>
              <div className="h-full rounded-full animate-pulse" style={{ width: '5%', background: 'var(--emp-accent)' }} />
            </div>
          </div>
        )}

        {/* Tabla boletas — altura natural según filas */}
        <div className="bla-table-wrap overflow-x-auto shrink-0" style={{ borderBottom: '2px solid var(--line)' }}>
          {loading ? (
            <div className="flex items-center justify-center py-12 gap-2" style={{ color: 'var(--ink-faint)' }}>
              <Loader2 size={20} className="animate-spin" />
              <span className="text-sm">Cargando boletas…</span>
            </div>
          ) : boletas.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 opacity-40">
              <FileText size={36} style={{ color: 'var(--ink-faint)' }} />
              <p className="text-sm" style={{ color: 'var(--ink-faint)' }}>
                {fechaDesde ? 'No hay boletas para esta semana — usá Generar para crearlas' : 'Ingresá la fecha de inicio de semana'}
              </p>
            </div>
          ) : (
            <>
            <table className="hidden md:table w-full text-xs" style={{ borderCollapse: 'collapse' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--surface-raised)' }}>
                <tr style={{ borderBottom: '1px solid var(--line)' }}>
                  <th className="px-2 py-2" style={{ width: 30 }}></th>
                  {COLS.map(col => (
                    <th key={col.key}
                      onClick={() => toggleSort(col.key)}
                      className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider whitespace-nowrap select-none cursor-pointer"
                      style={{ color: sortCol === col.key ? 'var(--emp-accent-txt)' : 'var(--ink-muted)' }}>
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        {sortCol === col.key
                          ? (sortDir === 'asc' ? <ChevronDown size={10} /> : <ChevronDown size={10} style={{ transform: 'rotate(180deg)' }} />)
                          : <ChevronDown size={10} style={{ opacity: 0.25 }} />}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {boletasOrdenadas.map((b, i) => {
                  const sel = boletaSel?.id === b.id;
                  const est = ESTADO_CFG[b.estado] ?? ESTADO_CFG.pendiente;
                  const expandido = expandidoId === b.id;
                  const productosExp = productosCache[b.id] ?? [];
                  const cargandoExp = loadingExpandido === b.id;
                  return (
                    <React.Fragment key={b.id}>
                    <tr
                      onClick={() => setBoletaSel(sel ? null : b)}
                      className="cursor-pointer transition-colors"
                      style={{
                        borderBottom: expandido ? 'none' : '1px solid var(--line-dim)',
                        background: sel ? 'var(--emp-accent-bg)' : i % 2 === 0 ? 'transparent' : 'var(--surface-deep)',
                      }}>
                      {/* Chevron acordeón */}
                      <td className="px-2 py-2 text-center" style={{ width: 30 }}>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleExpandido(b.id); }}
                          className="inline-flex items-center justify-center rounded transition-colors hover:bg-white/5"
                          style={{ width: 22, height: 22, color: expandido ? 'var(--emp-accent-txt)' : 'var(--ink-muted)' }}
                          title={expandido ? 'Cerrar detalle' : 'Ver detalle de productos'}
                        >
                          {expandido ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                      </td>
                      <td className="px-3 py-2 font-mono font-bold text-[11px]"
                        style={{ color: sel ? 'var(--emp-accent-txt)' : 'var(--emp-accent-txt)' }}>
                        {b.numero}
                      </td>
                      <td className="px-3 py-2">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold"
                          style={{ background: est.bg, border: `1px solid ${est.border}`, color: est.text }}>
                          {est.label}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono font-bold" style={{ color: 'var(--ink)' }}>{b.lote_num ?? '—'}</td>
                      <td className="px-3 py-2" style={{ color: 'var(--ink-muted)' }}>{b.finca_nombre ?? '—'}</td>
                      <td className="px-3 py-2 font-mono text-[11px]" style={{ color: 'var(--ink-muted)' }}>{fmtF(b.fecha_siembra)}</td>
                      <td className="px-3 py-2 font-mono text-[11px]" style={{ color: 'var(--ink-muted)' }}>{fmtF(b.fecha_plan)}</td>
                      <td className="px-3 py-2 font-mono text-[11px] font-semibold" style={{ color: 'var(--ink)' }}>{fmtF(b.fecha_aplicacion ?? b.fecha_plan)}</td>
                      <td className="px-3 py-2 font-mono text-[11px]" style={{ color: 'var(--ink-muted)' }}>
                        {b.boom_nombre
                          ? b.boom_nombre
                          : b.boom_capacidad_l
                            ? b.boom_capacidad_l.toLocaleString('es-CR') + ' L'
                            : '—'}
                      </td>
                      <td className="px-3 py-2 text-right font-mono" style={{ color: 'var(--ink-muted)' }}>{b.agua_l?.toLocaleString('es-CR') ?? '—'}</td>
                      <td className="px-3 py-2 text-right font-mono" style={{ color: 'var(--ink)' }}>{b.plantas?.toLocaleString('es-CR') ?? '—'}</td>
                      <td className="px-3 py-2 text-right font-mono" style={{ color: 'var(--ink-muted)' }}>{fmtN(b.area_ha)}</td>
                      <td className="px-3 py-2 text-[11px]" style={{ color: 'var(--ink-muted)', maxWidth: '10rem' }}>
                        {b.etapa_nombre}
                      </td>
                      <td className="px-3 py-2 text-[11px]" style={{ color: 'var(--ink-muted)' }}>{b.ciclo_nombre}</td>
                      <td className="px-3 py-2 text-right font-mono font-bold" style={{ color: 'var(--tone-warn-text)' }}>{b.dias ?? '—'}</td>
                      <td className="px-3 py-2 text-[11px] font-mono" style={{ color: 'var(--ink-faint)', maxWidth: '12rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {b.bloques}
                      </td>
                    </tr>
                    {expandido && (
                      <tr style={{ borderBottom: '1px solid var(--line)', background: 'var(--surface-deep)' }}>
                        <td colSpan={COLS.length + 1} style={{ padding: 0 }}>
                          <div style={{ borderLeft: '3px solid var(--emp-accent)' }}>
                            <div
                              className="text-[11px] font-bold uppercase tracking-widest"
                              style={{
                                color: 'var(--tone-info-text)',
                                background: 'var(--tone-info-bg)',
                                borderBottom: '1px solid var(--tone-info-border)',
                                padding: '8px 16px 8px 35px',
                              }}
                            >
                              Detalle de productos · #{b.numero} · Lote {b.lote_num} · {b.finca_nombre} · {b.etapa_nombre} / {b.ciclo_nombre}
                              <span className="font-mono ml-3" style={{ color: 'var(--ink-faint)', textTransform: 'none', letterSpacing: 'normal', fontWeight: 500 }}>
                                · {fmtN(b.area_ha)} ha · {b.n_booms_completos} boom{(b.n_booms_completos ?? 0) !== 1 ? 's' : ''} + {fmtN(b.litros_fraccion)} Lt fracción
                              </span>
                            </div>
                            <div style={{ padding: '8px 16px 12px 35px' }}>
                              {cargandoExp ? (
                                <div className="flex items-center gap-2 py-3" style={{ color: 'var(--ink-faint)' }}>
                                  <Loader2 size={14} className="animate-spin" /><span className="text-xs">Cargando productos…</span>
                                </div>
                              ) : productosExp.length === 0 ? (
                                <div className="py-3 text-xs opacity-50" style={{ color: 'var(--ink-faint)' }}>
                                  Sin productos registrados
                                </div>
                              ) : (
                                <table className="w-full text-xs" style={{ borderCollapse: 'collapse' }}>
                                  <thead>
                                    <tr style={{ borderBottom: '1px solid var(--line)' }}>
                                      {['#', 'Artículo', 'Nombre Producto', 'Descripción', 'Dosis', 'Boom', 'Fracción', 'Aplicar', 'Medida', 'Reingreso'].map(h => (
                                        <th key={h} className="px-3 py-1.5 text-left text-[10px] font-bold uppercase tracking-wider whitespace-nowrap"
                                          style={{ color: 'var(--ink-muted)' }}>{h}</th>
                                      ))}
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {productosExp.map((p, j) => (
                                      <tr key={p.id}
                                        style={{
                                          borderBottom: '1px solid var(--line-dim)',
                                          background: p.es_agua
                                            ? 'var(--tone-info-bg)'
                                            : j % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)',
                                        }}>
                                        <td className="px-3 py-1.5 font-mono text-[10px]" style={{ color: 'var(--ink-faint)' }}>{j + 1}</td>
                                        <td className="px-3 py-1.5 font-mono font-bold text-[11px]" style={{ color: 'var(--emp-accent-txt)' }}>{p.articulo}</td>
                                        <td className="px-3 py-1.5 font-medium" style={{ color: 'var(--ink)' }}>{p.nombre_producto}</td>
                                        <td className="px-3 py-1.5 text-[11px]" style={{ color: 'var(--ink-muted)' }}>{p.descripcion ?? '—'}</td>
                                        <td className="px-3 py-1.5 text-right font-mono" style={{ color: 'var(--ink)' }}>{fmtN(p.dosis, 3)}</td>
                                        <td className="px-3 py-1.5 text-right font-mono font-bold" style={{ color: 'var(--ink)' }}>{fmtN(p.cantidad_boom, 4)}</td>
                                        <td className="px-3 py-1.5 text-right font-mono" style={{ color: 'var(--ink-muted)' }}>{fmtN(p.cantidad_fraccion, 4)}</td>
                                        <td className="px-3 py-1.5 text-right font-mono font-bold" style={{ color: 'var(--tone-success-text)' }}>{fmtN(p.cantidad_aplicar, 4)}</td>
                                        <td className="px-3 py-1.5" style={{ color: 'var(--ink-muted)' }}>{p.medida ?? '—'}</td>
                                        <td className="px-3 py-1.5 text-[11px]" style={{ color: 'var(--ink-faint)' }}>{p.reingreso ?? '—'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>

            <div className="md:hidden space-y-3 p-3">
              {boletasOrdenadas.map((b) => {
                const sel = boletaSel?.id === b.id;
                const est = ESTADO_CFG[b.estado] ?? ESTADO_CFG.pendiente;
                const expandido = expandidoId === b.id;
                const productosExp = productosCache[b.id] ?? [];
                const cargandoExp = loadingExpandido === b.id;
                const boomTxt = b.boom_nombre
                  ? b.boom_nombre
                  : b.boom_capacidad_l
                    ? `${b.boom_capacidad_l.toLocaleString('es-CR')} L`
                    : '—';
                return (
                  <article
                    key={b.id}
                    className="rounded-lg border p-3"
                    style={{
                      background: sel ? 'var(--emp-accent-bg)' : 'var(--surface-raised)',
                      borderColor: sel ? 'var(--emp-accent)' : 'var(--line)',
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setBoletaSel(sel ? null : b)}
                      className="w-full text-left"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-mono text-sm font-bold" style={{ color: 'var(--emp-accent-txt)' }}>
                            #{b.numero}
                          </div>
                          <div className="mt-1 text-base font-bold" style={{ color: 'var(--ink)' }}>
                            Lote {b.lote_num ?? '—'}
                          </div>
                          <div className="mt-0.5 text-xs" style={{ color: 'var(--ink-muted)' }}>
                            {b.finca_nombre ?? '—'}
                          </div>
                        </div>
                        <span className="shrink-0 rounded px-2 py-1 text-[11px] font-semibold"
                          style={{ background: est.bg, border: `1px solid ${est.border}`, color: est.text }}>
                          {est.label}
                        </span>
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                        <MobileField label="Aplicar" value={fmtF(b.fecha_aplicacion ?? b.fecha_plan)} strong />
                        <MobileField label="Plan" value={fmtF(b.fecha_plan)} />
                        <MobileField label="Área" value={`${fmtN(b.area_ha)} ha`} strong />
                        <MobileField label="Plantas" value={b.plantas?.toLocaleString('es-CR') ?? '—'} />
                        <MobileField label="Etapa" value={b.etapa_nombre ?? '—'} />
                        <MobileField label="Ciclo" value={b.ciclo_nombre ?? '—'} />
                        <MobileField label="Boom" value={boomTxt} />
                        <MobileField label="Bloques" value={b.bloques || '—'} />
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => toggleExpandido(b.id)}
                      className="mt-3 flex min-h-[40px] w-full items-center justify-center gap-2 rounded-md border text-xs font-semibold"
                      style={{
                        borderColor: expandido ? 'var(--emp-accent)' : 'var(--line)',
                        background: expandido ? 'var(--emp-accent-bg)' : 'var(--surface-deep)',
                        color: expandido ? 'var(--emp-accent-txt)' : 'var(--ink-muted)',
                      }}
                    >
                      {expandido ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      {expandido ? 'Ocultar productos' : 'Ver productos'}
                    </button>

                    {expandido && (
                      <div className="mt-3 rounded-md border p-2" style={{ borderColor: 'var(--line)', background: 'var(--surface-deep)' }}>
                        {cargandoExp ? (
                          <div className="flex items-center justify-center gap-2 py-4 text-xs" style={{ color: 'var(--ink-faint)' }}>
                            <Loader2 size={14} className="animate-spin" /> Cargando productos...
                          </div>
                        ) : productosExp.length === 0 ? (
                          <div className="py-4 text-center text-xs" style={{ color: 'var(--ink-faint)' }}>
                            Sin productos registrados
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {productosExp.map((p) => (
                              <div key={p.id} className="rounded border p-2 text-xs" style={{
                                borderColor: p.es_agua ? 'var(--tone-info-border)' : 'var(--line-dim)',
                                background: p.es_agua ? 'var(--tone-info-bg)' : 'rgba(255,255,255,0.02)',
                              }}>
                                <div className="flex items-start justify-between gap-2">
                                  <div className="min-w-0">
                                    <div className="font-mono font-bold" style={{ color: 'var(--emp-accent-txt)' }}>{p.articulo}</div>
                                    <div className="font-semibold" style={{ color: 'var(--ink)' }}>{p.nombre_producto}</div>
                                    <div style={{ color: 'var(--ink-faint)' }}>{p.descripcion ?? '—'}</div>
                                  </div>
                                  <div className="shrink-0 text-right font-mono font-bold" style={{ color: 'var(--tone-success-text)' }}>
                                    {fmtN(p.cantidad_aplicar, 4)}
                                    <div className="text-[10px] font-normal" style={{ color: 'var(--ink-muted)' }}>{p.medida ?? '—'}</div>
                                  </div>
                                </div>
                                <div className="mt-2 grid grid-cols-3 gap-2">
                                  <MobileField label="Dosis" value={fmtN(p.dosis, 3)} />
                                  <MobileField label="Boom" value={fmtN(p.cantidad_boom, 4)} />
                                  <MobileField label="Fracción" value={fmtN(p.cantidad_fraccion, 4)} />
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </article>
                );
              })}
            </div>
            </>
          )}
        </div>

      </div>

      {toast && <Toast msg={toast.msg} ok={toast.ok} onClose={() => setToast(null)} />}
    </div>
  );
}

function MobileField({ label, value, strong = false }: { label: string; value: React.ReactNode; strong?: boolean }) {
  return (
    <div className="min-w-0 rounded border px-2 py-1.5" style={{ borderColor: 'var(--line-dim)', background: 'rgba(255,255,255,0.025)' }}>
      <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'var(--ink-faint)' }}>{label}</div>
      <div className={`truncate text-xs ${strong ? 'font-bold' : 'font-semibold'}`} style={{ color: strong ? 'var(--ink)' : 'var(--ink-muted)' }}>
        {value}
      </div>
    </div>
  );
}
